
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

export interface AnalyticsEvent {
  event_type: string;
  event_category: string;
  event_data?: Record<string, any>;
  page_path?: string;
  user_id?: string;
  session_id?: string;
}

export interface UserStateData {
  user_id: string;
  registration_date: string;
  last_activity: string;
  is_active_user: boolean;
  activity_count_7d: number;
  session_count: number;
  total_events: number;
}

class AnalyticsService {
  private eventBuffer: AnalyticsEvent[] = [];
  private sessionId: string | null = null;
  private batchSize = 1; // Immediate flush for critical events
  private flushInterval = 2000; // 2 seconds
  private flushTimer: NodeJS.Timeout | null = null;
  private lastFlush = Date.now();
  private isAdmin = false;
  private lastEventTime = new Map<string, number>();
  private sessionInitialized = false;
  
  constructor() {
    this.initializeService();
  }

  private async initializeSession(user?: User | null): Promise<string | null> {
    if (!user || this.sessionInitialized) {
      return this.sessionId;
    }

    try {
      const now = new Date().toISOString();
      const sessionId = crypto.randomUUID();
      
      // Create a proper session record in the database
      const { data: sessionData, error } = await supabase
        .from('user_sessions')
        .insert({
          session_id: sessionId,
          user_id: user.id,
          started_at: now,
          updated_at: now,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null
        })
        .select()
        .single();

      if (error) {
        console.error('[Analytics] Error creating session:', error);
        // If session creation fails, use null session_id
        this.sessionId = null;
      } else {
        this.sessionId = sessionData.session_id;
        console.log(`[Analytics] Session initialized: ${this.sessionId}`);
      }
      
      this.sessionInitialized = true;
      return this.sessionId;
    } catch (error) {
      console.error('[Analytics] Error in initializeSession:', error);
      this.sessionId = null;
      this.sessionInitialized = true;
      return null;
    }
  }

  private async checkAdminStatus(user?: User | null) {
    if (user?.email === 'grayholland@ux-flo.com') {
      this.isAdmin = true;
      console.log('[Analytics] Admin mode activated');
    }
  }

  private initializeService() {
    // Set up periodic flush
    this.flushTimer = setInterval(() => {
      this.flushEvents();
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flushEvents(true);
    });

    // Flush on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushEvents();
      }
    });
  }

  // Prevent only rapid duplicates within 500ms
  private isDuplicateEvent(event: AnalyticsEvent): boolean {
    const eventKey = `${event.event_type}:${event.event_category}`;
    const now = Date.now();
    const lastTime = this.lastEventTime.get(eventKey) || 0;
    
    if (now - lastTime < 500) {
      console.log('[Analytics] Preventing rapid duplicate:', event.event_type);
      return true;
    }
    
    this.lastEventTime.set(eventKey, now);
    return false;
  }

  // Enhanced tracking with better categorization
  async trackEvent(
    eventType: string, 
    eventCategory: string, 
    eventData?: Record<string, any>, 
    user?: User | null
  ) {
    // Skip noisy page view events from navigation context
    if (eventType === 'page_view' && eventCategory === 'navigation') {
      return;
    }

    // Initialize session if we have a user and haven't done so yet
    const sessionId = await this.initializeSession(user);

    const event: AnalyticsEvent = {
      event_type: eventType,
      event_category: eventCategory,
      event_data: eventData || {},
      page_path: window.location.pathname,
      user_id: user?.id,
      session_id: sessionId // Will be null if session creation failed
    };

    // Check admin status
    if (user) {
      this.checkAdminStatus(user);
    }

    // Skip rapid duplicates
    if (this.isDuplicateEvent(event)) {
      return;
    }

    console.log(`[Analytics] Tracking event: ${eventType} (${eventCategory})`, eventData);
    this.eventBuffer.push(event);

    // Critical Tier1 events get immediate flush
    const tier1Events = [
      'content_saved_manual', 'content_saved_extension', 'content_saved_import',
      'collection_created', 'tag_created', 'board_created',
      'content_added_to_collection', 'content_tagged', 'content_added_to_board',
      'content_organized', 'content_edited'
    ];

    const isTier1Event = tier1Events.includes(eventType) || 
                        eventCategory === 'content_saving' || 
                        eventCategory === 'organization' ||
                        eventCategory === 'curation';

    if (isTier1Event || this.isAdmin || this.eventBuffer.length >= this.batchSize) {
      console.log(`[Analytics] Immediate flush triggered for: ${eventType} (${eventCategory})`);
      this.flushEvents();
    }
  }

  // Enhanced flush with better error handling and logging
  private async flushEvents(forceSync = false) {
    if (this.eventBuffer.length === 0) return;

    const eventsToFlush = [...this.eventBuffer];
    this.eventBuffer = [];
    this.lastFlush = Date.now();

    console.log(`[Analytics] Flushing ${eventsToFlush.length} events:`, 
      eventsToFlush.map(e => `${e.event_type}:${e.event_category}`));

    try {
      const eventsWithTimestamp = eventsToFlush.map(event => ({
        event_type: event.event_type,
        event_category: event.event_category,
        event_data: event.event_data,
        page_path: event.page_path,
        user_id: event.user_id,
        session_id: event.session_id, // Can be null
        timestamp: new Date().toISOString()
      }));

      console.log('[Analytics] Inserting events:', eventsWithTimestamp);

      const { data, error } = await supabase
        .from('user_activity_events')
        .insert(eventsWithTimestamp)
        .select();

      if (error) {
        console.error('[Analytics] Error flushing events:', error);
        console.error('[Analytics] Failed events:', eventsWithTimestamp);
        
        // Only re-add non-duplicate events on non-constraint errors
        if (!error.message?.includes('duplicate') && !error.message?.includes('constraint')) {
          this.eventBuffer.unshift(...eventsToFlush.slice(0, 1));
        }
      } else {
        console.log(`[Analytics] Successfully flushed ${eventsToFlush.length} events to database`);
        console.log('[Analytics] Inserted data:', data);
      }
    } catch (error) {
      console.error('[Analytics] Network error flushing events:', error);
      this.eventBuffer.unshift(...eventsToFlush.slice(0, 1));
    }
  }

  // Force immediate flush
  async forceFlush() {
    console.log('[Analytics] Force flushing all buffered events');
    return this.flushEvents(true);
  }

  // Update user state - simplified to avoid session conflicts
  async updateUserState(user: User | null) {
    if (!user) return;

    await this.checkAdminStatus(user);
    // Session initialization now happens in trackEvent
    console.log(`[Analytics] User state updated - Admin: ${this.isAdmin}`);
  }

  // Get user analytics summary
  async getUserAnalytics(userId: string): Promise<UserStateData | null> {
    try {
      const { data: userData } = await supabase.auth.admin.getUserById(userId);
      if (!userData.user) return null;

      const { data: recentActivity } = await supabase
        .from('user_activity_events')
        .select('id')
        .eq('user_id', userId)
        .gte('timestamp', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

      const { data: totalEvents } = await supabase
        .from('user_activity_events')
        .select('id')
        .eq('user_id', userId);

      const { data: sessions } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('user_id', userId);

      return {
        user_id: userId,
        registration_date: userData.user.created_at,
        last_activity: new Date().toISOString(),
        is_active_user: (recentActivity?.length || 0) > 0,
        activity_count_7d: recentActivity?.length || 0,
        session_count: sessions?.length || 0,
        total_events: totalEvents?.length || 0
      };
    } catch (error) {
      console.error('[Analytics] Error getting user analytics:', error);
      return null;
    }
  }

  // Get buffer status for debugging
  getBufferStatus() {
    return {
      bufferSize: this.eventBuffer.length,
      maxBatchSize: this.batchSize,
      flushInterval: this.flushInterval,
      isAdmin: this.isAdmin,
      lastFlush: this.lastFlush,
      sessionId: this.sessionId,
      sessionInitialized: this.sessionInitialized
    };
  }

  // Clean up on destroy
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushEvents(true);
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
